import { useMutation } from '@tanstack/react-query';
import API from 'api';
import { QueryKeys } from 'api/models/QueryKeys';
import { queryClient } from 'components/QueryClientProvider/QueryClientProvider';

type PrintLabelData = Parameters<typeof API.deliveries.printLabel>[0];

export const usePrintLabel = () => {
  const query = useMutation({
    mutationFn: async (data: PrintLabelData) => {
      let response;

      // API call based on deliveryId and tray details
      if (data.deliveryId && data) {
        response = await API.deliveries.printLabel(data);
      } else {
        throw new Error('deliveryId is required');
      }

      // Handling response as Blob
      let pdfBlob: Blob;
      if (response instanceof Blob) {
        pdfBlob = response;
      } else if (response && (response as any).data instanceof Blob) {
        pdfBlob = (response as any).data;
      } else {
        throw new Error('Unexpected response format');
      }

      if (!(pdfBlob instanceof Blob)) {
        throw new Error('Response is not a Blob');
      }

      return pdfBlob;
    },
    onSuccess: async () => {
      // invalidate the delivery query
      await queryClient.invalidateQueries([QueryKeys.Delivery]);
    },
  });

  // Function to manually trigger the query and open the PDF in a new tab
  const openPrintTrayLabelsInNewTab = async (data: PrintLabelData) => {
    const result = await query.mutateAsync(data); // Wait for the refetch to complete

    // Ensure the query fetched data successfully
    if (result) {
      const pdfUrl = URL.createObjectURL(result);
      window.open(pdfUrl, '_blank'); // Open the PDF in a new tab
    } else {
      throw new Error('No data available to open PDF'); // Throw error if no data is available
    }
  };

  return {
    openPrintTrayLabelsInNewTab,
    loading: query.isLoading,
    error: query.error,
  };
};

export default usePrintLabel;
